import React, { useState } from 'react';
import axios from 'axios';
import useDeepCompareEffect from 'use-deep-compare-effect';
import useTranslation from 'next-translate/useTranslation';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { useAtom } from 'jotai';
import shipmentAtom, { serviceAtom } from '../../contexts/shipment';
import quotationAtom, {
  prohibitedCouriersAtom,
  prohibitedHsCodeMapAtom,
} from '../../contexts/quotation';
import { getQuotation } from '../../api/legacy';
import { determineProhibited } from '../../api/internal';
import useMessageHandler from '../../hooks/useMessageHandler';
import { getService, isShipmentDomestic } from './utils';
import { Heading, Separator, breakpoints } from '@spaceshipapp/cambridge';
import ServiceList from './ServiceList';
import Footer from './Footer';
import * as analytics from '../../analytics/quotation';
import Logger from '../../utils/logger';
import CarrierConstraintsRemarks from '../CarrierConstraintsRemarks';

interface Props {
  isSliderOpen: boolean;
  redirecting?: boolean;
  toggleSlider(): void;
  proceedBooking(): void;
}

const ServiceSelection: React.FC<Props> = ({
  isSliderOpen,
  redirecting,
  toggleSlider,
  proceedBooking,
  ...remain
}) => {
  const { t } = useTranslation('common');
  const { setError } = useMessageHandler();
  const [loadingQuotation, setLoadingQuotation] = useState(false);
  const [loadingProhibited, setLoadingProhibited] = useState(false);
  const [service] = useAtom(serviceAtom);
  const [options] = useAtom(quotationAtom);
  const [shipment] = useAtom(shipmentAtom);
  const { originCountry, destinationCountry, type: shipmentType, parcels, items } = shipment;
  const selectedService = getService(service, options);
  const [, setQuotation] = useAtom(quotationAtom);
  const [, setProhibitedHsCodeMap] = useAtom(prohibitedHsCodeMapAtom);
  const [, setProhibitedCouriers] = useAtom(prohibitedCouriersAtom);

  useDeepCompareEffect(() => {
    async function fetchQuotation() {
      try {
        setLoadingQuotation(true);
        const data = await getQuotation({
          originCountry,
          destinationCountry,
          parcels,
          shipmentType,
          hasItemsIncludesFood: items.some((it) => it.isIncludesFood),
          hasItemsIncludesBattery: items.some((it) => it.isIncludesBattery),
        });
        setQuotation(data);
        setLoadingQuotation(false);
        analytics.sendQuotationUpdated(shipment, selectedService);
      } catch (err: any) {
        setQuotation([]);
        if (!axios.isCancel(err)) {
          Logger.error(`[ServiceSelection: Quotation] - ${err}`);
          setError(err);
          setLoadingQuotation(false);
        }
      }
    }

    if (originCountry && destinationCountry) {
      if (isShipmentDomestic(originCountry, destinationCountry)) {
        // Domestic shipment is not supported in v2
        setQuotation([]);
      } else {
        fetchQuotation();
      }
    }
  }, [originCountry, destinationCountry, shipmentType, parcels, items]);

  useDeepCompareEffect(() => {
    async function fetchProhibitedList() {
      try {
        setLoadingProhibited(true);
        const data = await determineProhibited(items);
        const hsCodeMap = (data.items || []).reduce(
          (acc, { isProhibited, hkhsCode }) =>
            isProhibited && hkhsCode ? { ...acc, [hkhsCode]: isProhibited } : acc,
          {},
        );
        setProhibitedHsCodeMap(hsCodeMap);
        setProhibitedCouriers(data.excludedCouriers || []);
        setLoadingProhibited(false);
      } catch (err: any) {
        if (!axios.isCancel(err)) {
          Logger.error(`[ServiceSelection: ProhibitedList] - ${err}`);
          setProhibitedCouriers([]);
          setLoadingProhibited(false);
        }
      }
    }

    if (items.length > 0) {
      fetchProhibitedList();
    }
  }, [items]);

  return (
    <Container {...remain}>
      <CollapseContent isOpen={isSliderOpen}>
        <CarrierConstraintsRemarksWrapper>
          <CarrierConstraintsRemarks />
        </CarrierConstraintsRemarksWrapper>
        <Heading size="md" className={headingStyle}>
          {t('ServiceSelection.title')}
        </Heading>
        <ServiceList isOpen={isSliderOpen} />
        <Separator className={separatorStyle} />
      </CollapseContent>
      <Footer
        loading={loadingQuotation || loadingProhibited}
        redirecting={redirecting}
        isSliderOpen={isSliderOpen}
        toggleSlider={toggleSlider}
        proceedBooking={proceedBooking}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 44rem;
  margin: 0 auto;
`;

const headingStyle = css`
  margin-bottom: 1rem;

  @media (min-width: ${breakpoints.md}) {
    margin-bottom: 1.5rem;
  }
`;

const separatorStyle = css`
  margin-top: 1rem;
  margin-bottom: 1rem;

  @media (min-width: ${breakpoints.md}) {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

interface CollapseContentProps {
  isOpen: boolean;
}

const CollapseContent = styled.div<CollapseContentProps>`
  /* TODO: fix hardcoded value */
  height: calc(64vh + var(--safe-area-inset-bottom));
  max-height: ${(props) => (props.isOpen ? `calc(65vh + var(--safe-area-inset-bottom))` : `0`)};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: max-height 0.35s ease-in-out;

  @media (min-width: ${breakpoints.md}) {
    height: 80vh;
    max-height: ${(props) => (props.isOpen ? `80vh` : `0`)};
  }
`;

const CarrierConstraintsRemarksWrapper = styled.div`
  margin-bottom: 24px;
`;

export default ServiceSelection;
